import React, {createContext, useCallback, useContext, useReducer, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import useSafeDispatch from 'hooks/useSafeDispatch';

const MessageContext = createContext();
MessageContext.displayName = 'MessageContext';

const MessageDispatchContext = createContext();
MessageDispatchContext.displayName = 'MessageDispatchContext';

const actionTypes = {
  showMessage: 'showMessage'
};

const initialState = {
  type: undefined,
  message: null,
  duration: undefined
};

function messageReducer(state, action) {
  switch (action.type) {
    case actionTypes.showMessage: {
      return {
        ...state,
        ...action.payload
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

export {actionTypes, messageReducer};

const MessageProvider = ({children}) => {
  const [state, unsafeDispatch] = useReducer(messageReducer, initialState);

  const memoizedState = useMemo(() => state, [state]);
  const dispatch = useSafeDispatch(unsafeDispatch);

  return (
    <MessageContext.Provider value={memoizedState}>
      <MessageDispatchContext.Provider value={{dispatch}}>
        {children}
      </MessageDispatchContext.Provider>
    </MessageContext.Provider>
  );
};

function useMessageState() {
  const context = useContext(MessageContext);
  if (context === undefined) {
    throw new Error('useMessageState must be used within a MessageProvider');
  }

  return context;
}

function useMessageDispatch() {
  const context = useContext(MessageDispatchContext);
  const {t} = useTranslation();
  const trs = React.useCallback(t, []);

  if (context === undefined) {
    throw new Error('useMessageDispatch must be used within a MessageProvider');
  }

  const {dispatch} = context;

  const showMessage = useCallback(
    (payload) => {
      if (typeof payload?.message === 'string') {
        Object.assign(payload, {message: trs(payload.message)});
      }
      return dispatch({type: actionTypes.showMessage, payload});
    },
    [dispatch, trs]
  );

  return {
    dispatch,
    showMessage
  };
}

export {MessageContext, MessageProvider, useMessageState, useMessageDispatch};
